import { getPostPageMetadata } from '@wix/ambassador-blog-frontend-adapter-public-v2-post-page-http/http';
import { createAction, resolveId } from '@wix/communities-blog-client-common';
import type { CommonThunkAction } from '@app/types/common-thunk-action.type';
import { createPromisifiedAction } from '../actions-promisifier/create-promisified-action';
import { getLanguageCode } from '../selectors/locale-selectors';
import { getNormalizedPostSlug } from '../services/post-utils';
import { type NormalizedPost } from '../types';

export const FETCH_POST_METADATA_SUCCESS = 'postMetadata/FETCH_SUCCESS';
const FETCH_POST_METADATA_REQUEST = 'postMetadata/FETCH_REQUEST';
export const FETCH_POST_METADATA_FAILURE = 'postMetadata/FETCH_FAILURE';
export const RESET_POST_METADATA_LOADED = 'postMetadata/RESET_LOADED';

const fetchPostMetadataRequest = createAction(FETCH_POST_METADATA_REQUEST);
export const fetchPostMetadataSuccess = createAction(FETCH_POST_METADATA_SUCCESS);
export const resetPostMetadataLaoded = createAction(RESET_POST_METADATA_LOADED);
const fetchPostMetadataFailure = createAction(FETCH_POST_METADATA_FAILURE);

const buildDemoPostMetadata = (post: NormalizedPost) => {
  const postId = resolveId(post)!;
  const metrics = post.metrics;

  return {
    postPageMetadata: {
      id: postId,
      postMetadata: {
        viewCount: metrics?.views ?? post.viewCount ?? 0,
        totalComments: metrics?.comments ?? post.totalComments ?? 0,
        likeCount: metrics?.likes ?? post.likeCount ?? 0,
        liked: post.isLiked ?? false,
        averageRating: metrics?.averageRating ?? post.averageRating ?? 0,
        totalRatings: metrics?.totalRatings ?? post.totalRatings ?? 0,
      },
    },
    relatedPostsMetadata: [],
  };
};

export const fetchPostMetadata =
  (post: NormalizedPost): CommonThunkAction<unknown> =>
  async (dispatch, getState, { httpClient, errorHandler }) => {
    dispatch(fetchPostMetadataRequest());

    if (post.isDemo) {
      dispatch(fetchPostMetadataSuccess(buildDemoPostMetadata(post)));
      return;
    }

    const postSlug = getNormalizedPostSlug(post);

    if (!postSlug) {
      dispatch(fetchPostMetadataFailure(new Error('Post slug is not available')));
      return;
    }

    const languageCode = getLanguageCode(getState());
    const { withErrorHandler, getResolvedError } = errorHandler;

    try {
      const result = await withErrorHandler(
        () =>
          httpClient.request(
            getPostPageMetadata({ postId: postSlug, languageCode, skipViewCountIncrement: true }),
          ),
        { errorCodesMap: { applicationError: { PERMISSION_DENIED: false } } },
      );
      dispatch(fetchPostMetadataSuccess(result.data));
    } catch (error) {
      getResolvedError(error);
      dispatch(fetchPostMetadataFailure(error));
    }
  };

export const fetchPostMetadataPromisified = createPromisifiedAction(
  fetchPostMetadata,
  (result) => result,
);
